import { useMemo, useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";
import { CheckCircle2, Clock3, RefreshCw, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CrmDataProvider } from "../providers/types";
import {
  ECHO_REMAKE_IN_FLIGHT_STATUSES,
  type EchoflicksSessionRemake,
  type EchoflicksSessionRemakeStatus,
  type EchoflicksVideoSession,
} from "../types";
import { EmptyHint } from "./EchoflicksSessionList";
import { remakeCostLabel } from "./renderCosts";
import { formatDateTime } from "./sessionUtils";
import { RemakeVideoPlayer } from "./RemakeVideoPlayer";
import { remakeVideoSource, remakeWatchPath } from "./remakePlayback";

// How often to re-poll the remakes list while an attempt is queued/rendering.
const REMAKE_POLL_MS = 5000;

const IN_FLIGHT = new Set<string>(ECHO_REMAKE_IN_FLIGHT_STATUSES);

const isInFlight = (remake: EchoflicksSessionRemake) =>
  IN_FLIGHT.has(remake.status);

const anyInFlight = (remakes: EchoflicksSessionRemake[]) =>
  remakes.some(isInFlight);

/**
 * The "Remakes" card on the Echo page (admin-only). Lets an operator re-render a
 * past Echo through the current composer pipeline (generate-first), preview each
 * attempt inline, and Approve (deliver over WhatsApp) or Reject. Nothing reaches
 * WhatsApp until an explicit Approve, guarded server-side by the 24h window +
 * STOP suppression. See plans/2026-07-01-echo-remake-resend-plan.md §6.
 */
export const RemakesPreviewCard = ({
  record,
  selectedRemakeId,
}: {
  record: EchoflicksVideoSession;
  selectedRemakeId?: string;
}) => {
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  const sessionId = record.id;

  const { data, refetch, isPending, error } =
    useGetList<EchoflicksSessionRemake>(
      "echoflicks_remakes",
      {
        filter: { session_id: sessionId },
        sort: { field: "requested_at", order: "DESC" },
        pagination: { page: 1, perPage: 100 },
      },
      {
        enabled: sessionId != null,
        // Poll only while at least one attempt is still rendering; stop once every
        // attempt is terminal so a finished page isn't hammering the API.
        refetchInterval: (query) => {
          const rows =
            (
              query.state.data as
                | { data?: EchoflicksSessionRemake[] }
                | undefined
            )?.data ?? [];
          return anyInFlight(rows) ? REMAKE_POLL_MS : false;
        },
      },
    );

  const remakes = useMemo(() => data ?? [], [data]);
  const watchOnly = selectedRemakeId !== undefined;
  const visibleRemakes = watchOnly
    ? remakes.filter((remake) => String(remake.id) === selectedRemakeId)
    : remakes;
  const hasInFlight = anyInFlight(remakes);

  const operator = { id: identity?.id };

  const onRemake = async () => {
    setBusy(true);
    try {
      await dataProvider.triggerSessionRemake(sessionId, operator);
      notify("A new remake is rendering. It will appear below shortly.", {
        type: "info",
      });
      await refetch();
    } catch (error) {
      notify(errorMessage(error, "Could not start a remake"), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const onApprove = async (remake: EchoflicksSessionRemake) => {
    setBusy(true);
    try {
      const result = await dataProvider.approveSessionRemake(
        sessionId,
        remake.id,
        operator,
      );
      if (result.status === "skipped_out_of_window") {
        notify(
          "Skipped — the user is outside the 24h free-send window, so nothing was sent.",
          { type: "warning" },
        );
      } else {
        notify("Approved — delivering the remake over WhatsApp.", {
          type: "success",
        });
      }
      await refetch();
    } catch (error) {
      notify(errorMessage(error, "Could not approve the remake"), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const onReject = async (remake: EchoflicksSessionRemake) => {
    setBusy(true);
    try {
      await dataProvider.rejectSessionRemake(sessionId, remake.id, operator);
      notify("Rejected — this remake will not be sent.", { type: "info" });
      await refetch();
    } catch (error) {
      notify(errorMessage(error, "Could not reject the remake"), {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card id="remakes-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{watchOnly ? "Remake video" : "Remakes"}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {watchOnly
                ? `Remake ${selectedRemakeId}`
                : "Preview and watch remakes before or after delivery. Approve a reviewed attempt to send it over WhatsApp."}
            </p>
          </div>
          {!watchOnly ? (
            <Button
              type="button"
              size="sm"
              onClick={onRemake}
              disabled={busy || hasInFlight}
            >
              <RefreshCw
                className={`size-4 ${hasInFlight ? "animate-spin" : ""}`}
              />
              {hasInFlight ? "Rendering…" : "Remake"}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <div role="alert" className="space-y-2">
            <p>Could not load this Echo's remakes.</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => void refetch()}
            >
              Retry loading remakes
            </Button>
          </div>
        ) : isPending ? (
          <EmptyHint>Loading remakes…</EmptyHint>
        ) : visibleRemakes.length > 0 ? (
          <div className="space-y-4">
            {visibleRemakes.map((remake) => (
              <RemakeRow
                key={String(remake.id)}
                remake={remake}
                sessionId={sessionId}
                watchOnly={watchOnly}
                busy={busy}
                onApprove={() => onApprove(remake)}
                onReject={() => onReject(remake)}
              />
            ))}
          </div>
        ) : (
          <EmptyHint>
            {watchOnly
              ? "This remake was not found on this Echo."
              : "No remakes yet. Hit Remake to render this Echo again."}
          </EmptyHint>
        )}
      </CardContent>
    </Card>
  );
};

const RemakeRow = ({
  remake,
  sessionId,
  watchOnly,
  busy,
  onApprove,
  onReject,
}: {
  remake: EchoflicksSessionRemake;
  sessionId: string | number;
  watchOnly: boolean;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) => {
  // ECL-062: the remake's own render ledger (contract C5). No badge when the
  // API sends no cost fields; "Not recorded" (never $0) without a ledger.
  const cost = remakeCostLabel(remake);
  return (
    <div
      className="space-y-2 rounded-md border p-3"
      data-remake-id={String(remake.id)}
    >
      <div className="flex flex-wrap items-center gap-2">
        <RemakeStatusBadge status={remake.status} />
        {!watchOnly && remakeVideoSource(remake) ? (
          <a
            className="text-sm underline"
            href={`#${remakeWatchPath(sessionId, remake.id)}`}
          >
            Watch remake
          </a>
        ) : null}
        {cost ? (
          <Badge
            variant="outline"
            className="font-normal"
            title="This remake's own render ledger. The Echo cost card counts it under Edits and remakes."
          >
            {`Cost ${cost}`}
          </Badge>
        ) : null}
        {remake.requested_by ? (
          <span className="text-sm text-muted-foreground">
            by {remake.requested_by}
          </span>
        ) : null}
        {remake.requested_at ? (
          <span className="text-xs text-muted-foreground">
            {formatDateTime(remake.requested_at)}
          </span>
        ) : null}
      </div>

      {watchOnly || remakeVideoSource(remake) ? (
        <RemakeVideoPlayer remake={remake} />
      ) : null}

      {remake.status === "rendered" && !watchOnly ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={onApprove} disabled={busy}>
            <CheckCircle2 className="size-4" />
            Approve &amp; send
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onReject}
            disabled={busy}
          >
            <XCircle className="size-4" />
            Reject
          </Button>
        </div>
      ) : null}

      {remake.status === "delivered" ? (
        <div className="flex items-center gap-1.5 text-sm text-emerald-700">
          <CheckCircle2 className="size-4" />
          Delivered to the user over WhatsApp
          {remake.delivered_at
            ? ` on ${formatDateTime(remake.delivered_at)}`
            : ""}
          .
        </div>
      ) : null}

      {remake.status === "skipped_out_of_window" ? (
        <p className="text-sm text-amber-700">
          Not sent — the user was outside the 24h free-send window at approval
          time. Re-engaging them needs a template (the broadcast system).
        </p>
      ) : null}

      {remake.status === "rejected" ? (
        <p className="text-sm text-muted-foreground">
          Rejected — this attempt was not delivered.
        </p>
      ) : null}

      {remake.status === "submission_unknown" ? (
        <p className="text-sm text-amber-700">
          The render submission could not be confirmed. A retry never submits a
          second render; check the render queue before asking for another
          remake.
        </p>
      ) : null}

      {remake.status === "failed" ? (
        <p className="text-sm text-destructive">
          Render failed{remake.error ? `: ${remake.error}` : "."}
        </p>
      ) : null}

      {isInFlight(remake) ? (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock3 className="size-4" />
          {remake.status === "queued"
            ? "Queued — waiting for a render slot."
            : "Rendering — this usually takes a few minutes."}
        </p>
      ) : null}
    </div>
  );
};

const RemakeStatusBadge = ({
  status,
}: {
  status: EchoflicksSessionRemakeStatus | string;
}) => {
  const label = REMAKE_STATUS_LABELS[status] ?? status;
  const variant = REMAKE_STATUS_VARIANT[status] ?? "outline";
  return (
    <Badge variant={variant} className="font-normal">
      {label}
    </Badge>
  );
};

const REMAKE_STATUS_LABELS: Record<string, string> = {
  queued: "Queued",
  rendering: "Rendering",
  rendered: "Ready to review",
  delivered: "Delivered",
  delivery_queued: "Delivery queued",
  skipped_out_of_window: "Skipped (out of window)",
  rejected: "Rejected",
  failed: "Failed",
  submission_unknown: "Submission unknown",
};

const REMAKE_STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  queued: "outline",
  rendering: "secondary",
  rendered: "default",
  delivered: "secondary",
  skipped_out_of_window: "outline",
  rejected: "outline",
  failed: "destructive",
  submission_unknown: "secondary",
};

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
